type RawData = Record<string, unknown>;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// ---------------------------------------------------------------------------
// Backward-compat sets — only needed for data saved BEFORE the
// @physitrack/championhealth-types integration. Once all legacy
// in-progress assessments are completed these can be removed.
// ---------------------------------------------------------------------------

/**
 * Fields typed as `IntRangeString`, `NumericString`, or `Gad7Phq9Score`
 * in the package. Old web data stored them as numbers due to
 * `saveAssessmentValue` coercion; normalizing to strings on load.
 */
const LEGACY_NUMBER_TO_STRING = new Set([
  // GAD-7
  'nervous',
  'control_worrying',
  'worrying_too_much',
  'trouble_relaxing',
  'restless',
  'annoyed',
  'afraid',
  // PHQ-9
  'little_interest',
  'feeling_down',
  'trouble_falling_asleep',
  'feeling_tired',
  'poor_appetite',
  'feeling_bad',
  'trouble_concentrating',
  'slow',
  'self_harm',
  // Physical wellbeing
  'pain',
  'years',
  'months',
  'weeks',
  'overall_physical_health',
  // Movement
  'resistence_strength',
  'stretch',
  // Sleep
  'quality_of_sleep',
  // Stress
  'stress_at_moment',
  // Mental wellbeing
  'mood_rating',
  // Financial wellbeing (Likert 1–5)
  'confident_managing_finance',
  'relies_on_borrowing',
  'in_control_finances',
  'cannot_meet_financial_goals',
  // Nutrition
  'nutrition_quality',
  // Hydration
  'hydration',
  'caffeine',
  'dehydration',
  // Energy & productivity
  'energy_level',
  'productivity',
  'productivity_last_7',
  // Work support (Likert 1–5)
  'respected_work',
  'supported_work',
  'trusted_work',
  'listened_work',
  'part_of_team_work',
  // Goals
  'how_confident',
  'how_motivated',
]);

/**
 * Fields typed as `number` (IntRange) in the package. Old web data stored
 * them as strings because the former `saveAssessmentValue` did not coerce
 * NumberSelect output or because the value came from a text input.
 */
const LEGACY_STRING_TO_NUMBER = new Set([
  'age',
  'height_ft',
  'height_in',
  'height_cm',
  'weight_st',
  'weight_lb',
  'weight_kg',
  'red_meat',
  'vegetables',
  'takeaways',
  'smoke_per_day',
  'beers',
  'wines',
  'spirits',
  'hours_worked',
]);

/** Multi-select fields that may still carry `_sq`-suffixed item IDs. */
const SQ_MULTI_SELECT_FIELDS = new Set([
  'working_day_conditions',
  'past_mental_health_conditions',
  'money_worries_impact',
]);

/** Dismiss IDs that legitimately end with `_sq` in the package types. */
const LEGITIMATE_SQ_IDS = new Set(['none_sq']);

/**
 * Old web-specific "other" option IDs that differ from the package types.
 * Map them to the canonical `"other"` expected by `AssessmentData`.
 */
const LEGACY_ID_RENAMES: Record<string, Record<string, string>> = {
  work_aspects_stressful: { other_work_stress: 'other' },
  outside_work_stressful: { other_outside_pressure: 'other' },
};

const stripSqSuffix = (id: string): string => {
  if (LEGITIMATE_SQ_IDS.has(id)) return id;
  return id.replace(/_sq$/, '');
};

/**
 * Normalizes assessment data loaded from the API so the internal web
 * state matches `AssessmentData` from `@physitrack/championhealth-types`.
 *
 * Handles backward compatibility for data saved before the types integration:
 *
 * - `drink_alcohol: "no"` → `"never"` (old option id)
 * - Numeric primitives in string-typed fields → `String()` conversion
 * - Multi-select item IDs with stale `_sq` suffix → stripped
 */
export const normalizeFromApi = (data: RawData): RawData => {
  const normalized = { ...data };

  if (normalized.drink_alcohol === 'no') {
    normalized.drink_alcohol = 'never';
  }

  LEGACY_NUMBER_TO_STRING.forEach((field) => {
    const value = normalized[field];
    if (typeof value === 'number') {
      normalized[field] = String(value);
    }
  });

  LEGACY_STRING_TO_NUMBER.forEach((field) => {
    const value = normalized[field];
    if (typeof value === 'string') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        normalized[field] = num;
      }
    }
  });

  SQ_MULTI_SELECT_FIELDS.forEach((field) => {
    const value = normalized[field];
    if (!Array.isArray(value)) return;

    normalized[field] = value.map((item: unknown) => {
      if (isRecord(item) && typeof item.id === 'string' && item.id.endsWith('_sq')) {
        return { ...item, id: stripSqSuffix(item.id) };
      }
      return item;
    });
  });

  Object.entries(LEGACY_ID_RENAMES).forEach(([field, renames]) => {
    const value = normalized[field];
    if (!Array.isArray(value)) return;

    normalized[field] = value.map((item: unknown) => {
      if (isRecord(item) && typeof item.id === 'string' && renames[item.id]) {
        return { ...item, id: renames[item.id] };
      }
      return item;
    });
  });

  return normalized;
};
